import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { NavHeader } from "@/components/layout/nav-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput, type PhoneInputValue } from "@/components/ui/phone-input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, Calendar as CalendarIcon } from "lucide-react";
import { useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { VenueTimeFormats, parseVenueDateTime, VENUE_TIMEZONE } from "@/utils/timezone";
import { useBookingsDate } from "@/hooks/use-bookings-date";
import { useBookings } from "@/hooks/use-bookings";
import { useAuth } from "@/hooks/use-auth";
import { useBlockedDates } from "@/hooks/use-blocked-dates";
import { useBlockedTimeSlots } from "@/hooks/use-blocked-time-slots";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TIME_SLOTS_CONFIG } from "@/lib/constants";

// Create schema factory that takes isReturningArtist and hasPhoneNumber as parameters
const createBookingSchema = (isReturningArtist: boolean, hasPhoneNumber: boolean) => z.object({
  // Personal Info - only required for new artists
  artistName: isReturningArtist ? z.string().optional() : z.string().min(1, "Artist name is required"),
  location: isReturningArtist ? z.string().optional() : z.string().min(1, "City of origin is required"),
  phoneNumber: hasPhoneNumber 
    ? z.string().optional() 
    : z.string()
        .min(1, "Phone number is required")
        .refine((value) => {
          // Basic validation for international phone numbers
          // Must start with + and contain 10-15 digits
          return /^\+\d{10,15}$/.test(value.replace(/\s/g, ''));
        }, "Please enter a valid phone number with country code"),
  
  // Portfolio Links - only required for new artists
  instagramHandle: isReturningArtist ? z.string().optional() : z.string().min(1, "Instagram handle is required"),
  soundCloudLink: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  mixOrSpotifyLink: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  
  // Date & Time
  date: z.date({
    required_error: "Please select a date",
  }),
  timeSlot: z.string().min(1, "Please select a time slot"),
  duration: z.enum(["60", "90"]),
  
  // Artist Rider
  description: z.string().optional(),
  
  // Philanthropic Cause
  causeLinkType: z.enum(["gofundme", "custom", "none"]),
  selectedGofundmeId: z.number().optional(),
  causeName: z.string().optional(),
  causeLink: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  causeInstagramUrl: z.string().optional(),
}).superRefine((data, ctx) => {
  // Only validate music links for new artists
  if (!isReturningArtist && !data.soundCloudLink && !data.mixOrSpotifyLink) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please provide either a SoundCloud link or a Mix/Spotify playlist link",
      path: ["soundCloudLink"],
    });
  }
  
  // Validate cause link based on selection
  if (data.causeLinkType === "custom") {
    if (!data.causeLink) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide a custom cause link",
        path: ["causeLink"],
      });
    }
    if (!data.causeName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide a cause name",
        path: ["causeName"],
      });
    }
  } else if (data.causeLinkType === "gofundme" && !data.selectedGofundmeId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please select a GoFundMe link",
      path: ["selectedGofundmeId"],
    });
  }
});

type BookingFormValues = z.infer<ReturnType<typeof createBookingSchema>>;

// Generate time slots
const generateTimeSlots = (duration: 60 | 90) => {
  const slots: string[] = [];
  const startHour = TIME_SLOTS_CONFIG.START_HOUR;
  const endHour = TIME_SLOTS_CONFIG.END_HOUR;

  if (duration === 90) {
    for (let hour = startHour; hour < endHour; hour++) {
      slots.push(`${String(hour).padStart(2, "0")}:00`);
      if (hour < endHour - 1) {
        slots.push(`${String(hour).padStart(2, "0")}:30`);
      }
    }
    return slots;
  }

  for (let hour = startHour; hour < endHour; hour++) {
    slots.push(`${hour.toString().padStart(2, "0")}:00`);
    if (hour < endHour - 1) {
      slots.push(`${hour.toString().padStart(2, "0")}:30`);
    }
  }
  return slots;
};

export default function NewBookingPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const { user } = useAuth();

  // Ensure user data is fresh
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/user"] });
  }, [queryClient]);

  // Check if user has previous bookings
  const { data: allBookings } = useBookings();
  const userBookings = allBookings?.filter((booking: any) => booking.dj.id === user?.id) || [];
  const isReturningArtist = userBookings.length > 0;

  // Create schema based on current user state
  const bookingSchema = useMemo(() => {
    const hasPhoneNumber = !!(user?.phoneNumber && user.phoneNumber.trim() !== '');
    return createBookingSchema(isReturningArtist, hasPhoneNumber);
  }, [isReturningArtist, user?.phoneNumber]);

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      artistName: user?.displayName || "",
      location: user?.location || "",
      phoneNumber: user?.phoneNumber || "",
      instagramHandle: user?.instagramHandle || "",
      soundCloudLink: user?.soundCloudLink || "",
      mixOrSpotifyLink: user?.mixLink || "",
      duration: "60",
      description: "",
      causeLinkType: "gofundme",
      causeName: "",
      causeLink: "",
      causeInstagramUrl: "",
    },
  });

  const selectedDate = form.watch("date");
  const duration = parseInt(form.watch("duration"));
  const selectedTimeSlot = form.watch("timeSlot");

  // Fetch bookings for selected date
  const { data: bookings = [] } = useBookingsDate(selectedDate);

  // Fetch blocked dates
  const { data: blockedDates = [] } = useBlockedDates();
  
  // Fetch blocked time slots for selected date
  const { data: blockedTimeSlots = [] } = useBlockedTimeSlots(selectedDate);

  // Fetch GoFundMe links
  const { data: gofundmeLinks = [] } = useQuery({
    queryKey: ["gofundme-links"],
    queryFn: async () => {
      const res = await fetch("/api/admin/gofundme");
      if (!res.ok) throw new Error("Failed to fetch links");
      return res.json();
    },
  });

  // Check if time slot is booked or blocked
  const isTimeSlotUnavailable = (time: string): { unavailable: boolean; reason?: 'booked' | 'blocked' } => {
    if (!time || !selectedDate) return { unavailable: false };

    const [hours, minutes] = time.split(":").map(Number);
    
    // Create slot start/end times as venue time (no Z suffix)
    const datePart = format(selectedDate, "yyyy-MM-dd");
    const slotStartStr = `${datePart}T${time.padStart(5, '0')}:00.000`;
    const slotEndHour = Math.floor((hours * 60 + minutes + duration) / 60);
    const slotEndMinute = (hours * 60 + minutes + duration) % 60;
    const slotEndStr = `${datePart}T${String(slotEndHour).padStart(2, '0')}:${String(slotEndMinute).padStart(2, '0')}:00.000`;

    // Check if blocked by admin
    const isBlocked = blockedTimeSlots.some((blocked: any) => {
      // Parse the blocked times - they should already be in Pacific Time
      let blockedStartStr = blocked.startTime;
      let blockedEndStr = blocked.endTime;
      
      // Remove Z suffix if present (for backwards compatibility)
      if (blockedStartStr.endsWith('Z')) {
        blockedStartStr = blockedStartStr.slice(0, -1);
      }
      if (blockedEndStr.endsWith('Z')) {
        blockedEndStr = blockedEndStr.slice(0, -1);
      }
      
      // Extract just the date and time parts for comparison
      const blockedStart = blockedStartStr.replace('T', ' ').substring(0, 19);
      const blockedEnd = blockedEndStr.replace('T', ' ').substring(0, 19);
      const slotStart = slotStartStr.replace('T', ' ').substring(0, 19);
      const slotEnd = slotEndStr.replace('T', ' ').substring(0, 19);

      // Check for overlap using string comparison
      return (
        (slotStart >= blockedStart && slotStart < blockedEnd) ||
        (slotEnd > blockedStart && slotEnd <= blockedEnd) ||
        (slotStart <= blockedStart && slotEnd >= blockedEnd)
      );
    });

    if (isBlocked) {
      return { unavailable: true, reason: 'blocked' };
    }

    // Check if booked by another user
    const isBooked = bookings && bookings.some((booking: any) => {
      // Only consider bookings with pending_approval, scheduled, or no_show status as occupied
      if (booking.lifecycleStatus !== "pending_approval" && 
          booking.lifecycleStatus !== "scheduled" && 
          booking.lifecycleStatus !== "no_show") {
        return false;
      }

      // Remove Z suffix from booking times for direct comparison
      const bookingStartStr = booking.timeSlot.startTime.replace('Z', '');
      const bookingEndStr = booking.timeSlot.endTime.replace('Z', '');

      // Check for overlap using string comparison (works because ISO format)
      return (
        (slotStartStr >= bookingStartStr && slotStartStr < bookingEndStr) ||
        (slotEndStr > bookingStartStr && slotEndStr <= bookingEndStr) ||
        (slotStartStr <= bookingStartStr && slotEndStr >= bookingEndStr)
      );
    });

    if (isBooked) {
      return { unavailable: true, reason: 'booked' };
    }

    return { unavailable: false };
  };

  // Format time slot for display
  const formatTimeSlotForDisplay = (timeSlot: string): string => {
    if (!selectedDate) return timeSlot;
    const datePart = format(selectedDate, "yyyy-MM-dd");
    
    try {
      const dateTime = parseVenueDateTime(datePart, timeSlot);
      // Use timeOnly format which includes PT indicator
      return VenueTimeFormats.timeOnly(dateTime);
    } catch (error) {
      return timeSlot;
    }
  };

  // Submit mutation
  const bookingMutation = useMutation({
    mutationFn: async (values: BookingFormValues) => {
      const response = await fetch("/api/bookings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: values.artistName,
          location: values.location,
          phoneNumber: values.phoneNumber,
          instagramHandle: values.instagramHandle,
          soundCloudLink: values.soundCloudLink,
          mixOrSpotifyLink: values.mixOrSpotifyLink,
          causeLink: values.causeLinkType === "custom" ? values.causeLink : undefined,
          selectedGofundmeId: values.causeLinkType === "gofundme" ? values.selectedGofundmeId : undefined,
          description: values.description,
          useCustomCauseLink: values.causeLinkType === "custom",
          date: values.date,
          timeSlot: values.timeSlot,
          duration: parseInt(values.duration),
          causeName: values.causeLinkType === "none" ? undefined : values.causeName,
          causeInstagramUrl: values.causeLinkType === "none" ? undefined : values.causeInstagramUrl,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create booking");
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Booking Request Submitted",
        description: "Your booking request has been submitted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      setLocation("/my-bookings");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: BookingFormValues) => {
    bookingMutation.mutate(data);
  };

  const handleCancel = () => {
    setLocation("/");
  };

  // Date picker configuration
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentMonth = new Date();
  currentMonth.setDate(1);

  // Check if a date is blocked
  const isDateBlocked = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return blockedDates.some((blocked: any) => {
      const blockedDateStr = blocked.date.split('T')[0];
      return blockedDateStr === dateStr;
    });
  };

  const disabledDays = [
    { before: today },
    (date: Date) => {
      const day = date.getDay();
      return day === 0 || day === 6;
    },
    (date: Date) => isDateBlocked(date),
  ];

  // TODO: Remove weekday evening block when ready to open those slots
  const timeSlots = generateTimeSlots(duration as 60 | 90).filter((slot) => {
    if (!selectedDate) return true;
    const day = selectedDate.getDay();
    const isWeekday = day >= 1 && day <= 5;
    if (!isWeekday) return true;
    const hour = parseInt(slot.split(":")[0], 10);
    const minute = parseInt(slot.split(":")[1], 10);
    return hour < 18 || (hour === 18 && minute === 0);
  });

  return (
    <div className="min-h-screen bg-clubhouse-black text-white">
      <NavHeader />
      
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start gap-4 pb-6 border-b border-white/10 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowCancelDialog(true)}
              className="text-white/40 hover:text-white hover:bg-transparent p-0 mt-1"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold mb-2">New booking</h1>
              <p className="text-white/40">Book your timeslot to perform at the clubhouse.</p>
            </div>
          </div>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 animate-stagger">
            {/* Personal Info - only show for new artists */}
            {!isReturningArtist && (
              <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Personal Info</h2>
                  <p className="text-sm text-white/40">Tell us a bit about yourself.</p>
                </div>
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <Label htmlFor="artistName">Artist Name</Label>
                    <Input
                      id="artistName"
                      {...form.register("artistName")}
                      placeholder="Enter your artist name..."
                      className="mt-1"
                    />
                    {form.formState.errors.artistName && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.artistName.message}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Your DJ/artist name to display on our channel and socials.</p>
                  </div>

                  <div>
                    <Label htmlFor="location">City of Origin</Label>
                    <Input
                      id="location"
                      {...form.register("location")}
                      placeholder="Search by city..."
                      className="mt-1"
                    />
                    {form.formState.errors.location && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.location.message}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Where are you from? Our IG post will say "From: X"</p>
                  </div>

                  <div>
                    <Label htmlFor="phoneNumber">Phone Number</Label>
                    <PhoneInput
                      value={form.watch("phoneNumber")}
                      onChange={(value) => form.setValue("phoneNumber", value)}
                      className="mt-1"
                      placeholder="Enter phone number"
                      defaultCountry="us"
                    />
                    <p className="text-xs text-gray-500 mt-1">So we can contact you once we confirm a time.</p>
                    {form.formState.errors.phoneNumber && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.phoneNumber.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Portfolio Links - only show for new artists */}
            {!isReturningArtist && (
              <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Portfolio Links</h2>
                  <p className="text-sm text-white/40">Give us some insight into what your sound is.</p>
                </div>
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <Label htmlFor="instagramHandle">Instagram Handle</Label>
                    <Input
                      id="instagramHandle"
                      {...form.register("instagramHandle")}
                      placeholder="yourusername"
                      className="mt-1"
                    />
                    {form.formState.errors.instagramHandle && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.instagramHandle.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="soundCloudLink">Soundcloud Link</Label>
                    <Input
                      id="soundCloudLink"
                      {...form.register("soundCloudLink")}
                      placeholder="https://"
                      className="mt-1"
                    />
                    {form.formState.errors.soundCloudLink && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.soundCloudLink.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="mixOrSpotifyLink">Mix or Spotify Playlist Link</Label>
                    <Input
                      id="mixOrSpotifyLink"
                      {...form.register("mixOrSpotifyLink")}
                      placeholder="https://"
                      className="mt-1"
                    />
                    {form.formState.errors.mixOrSpotifyLink && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.mixOrSpotifyLink.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Contact Information - show only for returning artists without phone number */}
            {isReturningArtist && (!user?.phoneNumber || user?.phoneNumber.trim() === '') && (
              <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div>
                    <h2 className="text-lg font-semibold mb-1">Contact Information</h2>
                    <p className="text-sm text-white/40">So we can reach you about your booking.</p>
                  </div>
                  <div className="md:col-span-2">
                    <div>
                      <Label htmlFor="phoneNumber">Phone Number</Label>
                      <PhoneInput
                        value={form.watch("phoneNumber")}
                        onChange={(value) => form.setValue("phoneNumber", value)}
                        className="mt-1"
                        placeholder="Enter phone number"
                        defaultCountry="us"
                      />
                      <p className="text-xs text-gray-500 mt-1">So we can contact you once we confirm a time.</p>
                      {form.formState.errors.phoneNumber && (
                        <p className="text-red-500 text-sm mt-1">{form.formState.errors.phoneNumber.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Date & Time */}
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Date & Time</h2>
                  <p className="text-sm text-white/40">Let us know when you can perform.</p>
                </div>
                <div className="md:col-span-2 space-y-4">
                  <div>
                    <Label>Time Slot</Label>
                    <RadioGroup
                      value={form.watch("duration")}
                      onValueChange={(value) => form.setValue("duration", value as "60" | "90")}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center">
                        <RadioGroupItem value="60" id="60min" />
                        <Label htmlFor="60min" className="ml-2 font-normal cursor-pointer">60 mins</Label>
                      </div>
                      <div className="flex items-center">
                        <RadioGroupItem value="90" id="90min" />
                        <Label htmlFor="90min" className="ml-2 font-normal cursor-pointer">90+ mins</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div>
                    <Label>Desired Date</Label>
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal mt-1",
                            !selectedDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedDate ? format(selectedDate, "EEE, MMM dd, yyyy") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => {
                            if (date) {
                              form.setValue("date", date);
                              setDatePickerOpen(false);
                            }
                          }}
                          disabled={disabledDays}
                          initialFocus
                          className=""
                        />
                      </PopoverContent>
                    </Popover>
                    {form.formState.errors.date && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.date.message}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="timeSlot">Desired Time</Label>
                    <Select
                      value={selectedTimeSlot}
                      onValueChange={(value) => form.setValue("timeSlot", value)}
                      disabled={!selectedDate}
                    >
                      <SelectTrigger className="mt-1" disabled={!selectedDate}>
                        <SelectValue placeholder={selectedDate ? "Select time" : "Select a date first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => {
                          const availability = isTimeSlotUnavailable(slot);
                          return (
                            <SelectItem
                              key={slot}
                              value={slot}
                              disabled={availability.unavailable}
                            >
                              {formatTimeSlotForDisplay(slot)} 
                              {availability.unavailable && (
                                availability.reason === 'blocked' ? ' (Blocked)' : ' (Booked)'
                              )}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {form.formState.errors.timeSlot && (
                      <p className="text-red-500 text-sm mt-1">{form.formState.errors.timeSlot.message}</p>
                    )}
                    {!selectedDate && (
                      <p className="text-xs text-gray-500 mt-1">Please select a date to see available time slots</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Artist Notes */}
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Artist Notes</h2>
                  <p className="text-sm text-white/40">Describe your performance and any special requirements.</p>
                </div>
                <div className="md:col-span-2">
                  <Textarea
                    {...form.register("description")}
                    placeholder="Type here..."
                    className="min-h-[100px]"
                  />
                </div>
              </div>
            </div>

            {/* Philanthropic Cause */}
            <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h2 className="text-lg font-semibold mb-1">Philanthropic Cause (Optional)</h2>
                  <p className="text-sm text-white/40">Your stream will promote a charity of your choice.</p>
                </div>
                <div className="md:col-span-2 space-y-4">
                  <RadioGroup
                    value={form.watch("causeLinkType")}
                    onValueChange={(value) => form.setValue("causeLinkType", value as "gofundme" | "custom" | "none")}
                    className="flex flex-col sm:flex-row gap-4"
                  >
                    <div className="flex items-center">
                      <RadioGroupItem value="gofundme" id="gofundme" />
                      <Label htmlFor="gofundme" className="ml-2 font-normal cursor-pointer">Curated from list</Label>
                    </div>
                    <div className="flex items-center">
                      <RadioGroupItem value="custom" id="custom" />
                      <Label htmlFor="custom" className="ml-2 font-normal cursor-pointer">Custom link</Label>
                    </div>
                    <div className="flex items-center">
                      <RadioGroupItem value="none" id="none" />
                      <Label htmlFor="none" className="ml-2 font-normal cursor-pointer">None</Label>
                    </div>
                  </RadioGroup>

                  {form.watch("causeLinkType") === "none" ? null : form.watch("causeLinkType") === "gofundme" ? (
                    <div>
                      <Label>Select from curated causes</Label>
                      <Select
                        value={form.watch("selectedGofundmeId")?.toString()}
                        onValueChange={(value) => {
                          const selectedLink = gofundmeLinks.find((link: any) => link.id === parseInt(value));
                          form.setValue("selectedGofundmeId", parseInt(value));
                          if (selectedLink) {
                            form.setValue("causeName", selectedLink.name);
                            // Set Instagram URL from the selected GoFundMe link
                            form.setValue("causeInstagramUrl", selectedLink.instagramUrl || "");
                          }
                        }}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select a cause" />
                        </SelectTrigger>
                        <SelectContent>
                          {gofundmeLinks.map((link: any) => (
                            <SelectItem key={link.id} value={link.id.toString()}>
                              {link.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.formState.errors.selectedGofundmeId && (
                        <p className="text-red-500 text-sm mt-1">{form.formState.errors.selectedGofundmeId.message}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label htmlFor="causeName">Name of Charity/Cause</Label>
                        <Input
                          id="causeName"
                          {...form.register("causeName")}
                          placeholder="Charity/Cause"
                          className="mt-1"
                        />
                        {form.formState.errors.causeName && (
                          <p className="text-red-500 text-sm mt-1">{form.formState.errors.causeName.message}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="causeLink">Donations Page URL</Label>
                        <Input
                          id="causeLink"
                          {...form.register("causeLink")}
                          placeholder="https://"
                          className="mt-1"
                        />
                        {form.formState.errors.causeLink && (
                          <p className="text-red-500 text-sm mt-1">{form.formState.errors.causeLink.message}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="causeInstagramUrl">Instagram Page of Charity/Cause (Optional)</Label>
                        <Input
                          id="causeInstagramUrl"
                          {...form.register("causeInstagramUrl")}
                          placeholder="https://instagram.com/CharityCause"
                          className="mt-1"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pb-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCancelDialog(true)}
                className="w-full sm:w-auto border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="w-full sm:w-auto bg-white hover:bg-white/90 active:scale-[0.97] text-black transition-all"
                disabled={bookingMutation.isPending}
              >
                {bookingMutation.isPending ? "Submitting..." : "Submit request"}
              </Button>
            </div>
          </form>
        </div>

        {/* Cancel Confirmation Dialog */}
        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel booking?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel? All your progress will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/[0.08] bg-white/[0.04] hover:bg-white/[0.08] text-white">
                Continue editing
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancel}
                className="bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25"
              >
                Cancel booking
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}